import { useState, useEffect } from 'react'

/**
 * Custom hook to detect when an image element has loaded within a container
 * Useful for third-party components that don't expose image load events
 *
 * @param {Object} containerRef - React ref to the container element
 * @param {string} imageSource - The image source URL to watch for changes
 * @returns {boolean} imageLoaded - True when the image has finished loading
 */
const useImageLoaded = (containerRef, imageSource) => {
  const [imageLoaded, setImageLoaded] = useState(false)

  useEffect(() => {
    if (!imageSource) {
      setImageLoaded(false)
      return
    }

    setImageLoaded(false)

    const checkImageLoaded = () => {
      const imgElement = containerRef.current?.querySelector('img')
      if (imgElement) {
        if (imgElement.complete && imgElement.naturalHeight !== 0) {
          setImageLoaded(true)
        } else {
          imgElement.addEventListener('load', () => setImageLoaded(true), { once: true })
          imgElement.addEventListener('error', () => setImageLoaded(true), { once: true })
        }
        return true
      }
      return false
    }

    // Use MutationObserver to watch for when the img element gets added to DOM
    let observer
    let attempts = 0
    const maxAttempts = 50 // 5 seconds max

    const intervalId = setInterval(() => {
      if (checkImageLoaded() || attempts >= maxAttempts) {
        clearInterval(intervalId)
        if (observer) observer.disconnect()
      }
      attempts++
    }, 100)

    // Also use MutationObserver for immediate detection
    if (containerRef.current) {
      observer = new MutationObserver(() => {
        if (checkImageLoaded()) {
          clearInterval(intervalId)
          observer.disconnect()
        }
      })

      observer.observe(containerRef.current, {
        childList: true,
        subtree: true
      })
    }

    return () => {
      clearInterval(intervalId)
      if (observer) observer.disconnect()
    }
  }, [containerRef, imageSource])

  return imageLoaded
}

export default useImageLoaded
